/**
 * Blunder Test Suite Panel
 * Matrix view: run ALL 21 AI variants against each saved blunder.
 * Shows pass/fail grid per blunder with drill-down detail.
 */

import React, { useState, useCallback, useRef } from 'react';
import styles from './BlunderTestSuitePanel.module.css';
import {
  BlunderTestCase,
  TestSuiteResult,
  getTestCases,
  deleteTestCase,
  saveTestResult,
  isMoveBlunder,
  exportTestSuite,
  importTestSuite,
} from '../../services/AIArena/BlunderTestSuite';
import { ZodiacEngine } from '../../services/zodiacEngine/zodiacEngine';
import { loadTestScenarioFEN } from '../../services/zodiacEngine/Fen/FenLoader';
import { AIOrchestrator } from '../../services/zodiacEngine/AI/Core/AIOrchestrator';
import { AIPlayerType } from '../../services/zodiacEngine/AI/Core/IAIPlayer';
import {
  AIModel,
  AI_MODEL_CONFIG,
  TimeFrame,
  TIME_FRAME_CONFIG,
  getBaseAITypeFromModel,
} from '../../services/zodiacEngine/AI/Core/AIVariants';
import { NAMED_VARIANTS } from '../../services/AIArena/AIArenaConstants';

interface BlunderTestSuitePanelProps {
  onLoadPosition: (fen: string, lightKing: string, darkKing: string) => void;
  refreshKey: number;
}

// All implemented models
const IMPLEMENTED_MODELS: AIModel[] = [
  AIModel.GAIA, AIModel.SENTINEL, AIModel.ALCHEMIST,
  AIModel.ZENITH, AIModel.HUNTER, AIModel.ARROW, AIModel.WISP, AIModel.CREATURE,
];

interface TestVariant {
  key: string;
  model: AIModel;
  timeFrame: TimeFrame;
  label: string;
  timeLimit: number;
}

// Build all 21 named variants
const ALL_VARIANTS: TestVariant[] = [];
for (const model of IMPLEMENTED_MODELS) {
  for (const tf of Object.values(TimeFrame)) {
    const key = `${model}-${tf}`;
    const tfConfig = TIME_FRAME_CONFIG[tf];
    const label = NAMED_VARIANTS[key] || `${AI_MODEL_CONFIG[model].displayName} (${tfConfig.timeLimit / 1000}s)`;
    ALL_VARIANTS.push({ key, model, timeFrame: tf, label, timeLimit: tfConfig.timeLimit });
  }
}

const colLetters = 'ABCDE';

const severityColor: Record<string, string> = {
  critical: '#ef4444',
  major: '#f59e0b',
  minor: '#64748b',
};

export default function BlunderTestSuitePanel({ onLoadPosition, refreshKey }: BlunderTestSuitePanelProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // allResults[testCaseId][variantKey] = TestSuiteResult
  const [allResults, setAllResults] = useState<Record<string, Record<string, TestSuiteResult>>>({});
  const [selectedCell, setSelectedCell] = useState<{ testCaseId: string; variantKey: string } | null>(null);
  const [runningLabel, setRunningLabel] = useState<string | null>(null);
  const [runProgress, setRunProgress] = useState<{ done: number; total: number } | null>(null);
  const importRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef(false);

  const testCases = getTestCases();

  // Run a single test: one blunder × one variant
  const runSingleTest = useCallback(async (tc: BlunderTestCase, variant: TestVariant): Promise<TestSuiteResult | null> => {
    try {
      const engine = new ZodiacEngine();
      engine.setVerboseLogging(false);
      loadTestScenarioFEN(engine, tc.fen);

      // FEN from game history uses encodeWithoutKings — king markers are stripped.
      // Restore isKing flag using the known king types from the test case.
      const realState = (engine as any).gameStateManager.getGameState();
      for (let row = 0; row < realState.board.length; row++) {
        for (let col = 0; col < realState.board[row].length; col++) {
          for (const piece of realState.board[row][col].Pieces) {
            if (piece.isLight && piece.type === tc.lightKing) {
              piece.isKing = true;
              realState.lightKing = piece.type;
            } else if (!piece.isLight && piece.type === tc.darkKing) {
              piece.isKing = true;
              realState.darkKing = piece.type;
            }
          }
        }
      }

      const orchestrator = new AIOrchestrator(engine);
      const baseType = getBaseAITypeFromModel(variant.model);
      const gs = engine.getGameState();

      const isLight = tc.testSide === 'light';
      gs.isLightTurn = isLight;
      // CRITICAL: Set on the real engine state — engine.getGameState() returns a shallow copy
      (engine as any).gameStateManager.getGameState().isLightTurn = isLight;

      if (isLight) {
        orchestrator.setLightPlayer(baseType);
      } else {
        orchestrator.setDarkPlayer(baseType);
      }

      const config = AI_MODEL_CONFIG[variant.model];
      if (config.minimaxModel && baseType === AIPlayerType.MINIMAX) {
        const minimaxAI = orchestrator.getAI(AIPlayerType.MINIMAX) as any;
        if (minimaxAI?.setModel) minimaxAI.setModel(config.minimaxModel);
      }

      const moveResult = await orchestrator.requestAIMove(gs, variant.timeLimit);
      if (!moveResult) return null;

      const fromPos = moveResult.fromPosition;
      const toPos = moveResult.move.position;
      const fromLabel = `${colLetters[fromPos.col]}${fromPos.row + 1}`;
      const toLabel = `${colLetters[toPos.col]}${toPos.row + 1}`;
      const fromSquare = gs.board[fromPos.row]?.[fromPos.col];
      const actualPiece = fromSquare?.Pieces.find(p => p.id === moveResult.move.pieceId);
      const side = actualPiece ? (actualPiece.isLight ? 'Light' : 'Dark') : (isLight ? 'Light' : 'Dark');
      const pieceName = moveResult.movingPiece?.type || 'Unknown';
      const captured = moveResult.move.toPiece ? ` captures ${moveResult.move.toPiece.type}` : '';

      const isBlunderResult = isMoveBlunder(tc, fromLabel, toLabel);

      const result: TestSuiteResult = {
        testCaseId: tc.id,
        variantKey: variant.key,
        chosenMove: {
          from: fromLabel,
          to: toLabel,
          description: `${side} ${pieceName} ${fromLabel}\u2192${toLabel}${captured}`,
        },
        evaluation: moveResult.evaluation ?? 0,
        confidence: moveResult.confidence,
        thinkingTime: moveResult.thinkingTime,
        isBlunder: isBlunderResult === true,
        reasoning: moveResult.reasoning,
        timestamp: new Date().toISOString(),
      };
      saveTestResult(result);
      return result;
    } catch (err) {
      return null;
    }
  }, []);

  // Run all 21 variants for one blunder
  const handleRunBlunder = useCallback(async (tc: BlunderTestCase) => {
    abortRef.current = false;
    setRunProgress({ done: 0, total: ALL_VARIANTS.length });

    for (let i = 0; i < ALL_VARIANTS.length; i++) {
      if (abortRef.current) break;
      const v = ALL_VARIANTS[i];
      setRunningLabel(v.label);
      const result = await runSingleTest(tc, v);
      if (result) {
        setAllResults(prev => ({
          ...prev,
          [tc.id]: { ...(prev[tc.id] || {}), [v.key]: result },
        }));
      }
      setRunProgress({ done: i + 1, total: ALL_VARIANTS.length });
    }

    setRunningLabel(null);
    setRunProgress(null);
  }, [runSingleTest]);

  // Run all blunders × all variants
  const handleRunAll = useCallback(async () => {
    abortRef.current = false;
    const cases = getTestCases();
    const totalTests = cases.length * ALL_VARIANTS.length;
    let done = 0;
    setRunProgress({ done: 0, total: totalTests });

    for (const tc of cases) {
      if (abortRef.current) break;
      for (const v of ALL_VARIANTS) {
        if (abortRef.current) break;
        setRunningLabel(`${tc.name} / ${v.label}`);
        const result = await runSingleTest(tc, v);
        if (result) {
          setAllResults(prev => ({
            ...prev,
            [tc.id]: { ...(prev[tc.id] || {}), [v.key]: result },
          }));
        }
        done++;
        setRunProgress({ done, total: totalTests });
      }
    }

    setRunningLabel(null);
    setRunProgress(null);
  }, [runSingleTest]);

  const handleExport = useCallback(() => {
    const json = exportTestSuite();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `blunder-test-suite-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const handleImport = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const result = importTestSuite(reader.result as string);
        alert(`Imported ${result.casesImported} test cases, ${result.resultsImported} results`);
      } catch (err) {
        alert(`Import failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }, []);

  const handleDelete = useCallback((id: string) => {
    deleteTestCase(id);
    setAllResults(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    if (selectedCell?.testCaseId === id) setSelectedCell(null);
    if (expandedId === id) setExpandedId(null);
  }, [selectedCell, expandedId]);

  const isRunning = !!runningLabel;

  return (
    <div className={styles.panel}>
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.headerTitle}>Blunder Test Suite ({testCases.length})</span>
        <div className={styles.headerActions}>
          <button className={styles.actionBtn} onClick={handleExport} disabled={testCases.length === 0}>Export</button>
          <button className={styles.actionBtn} onClick={() => importRef.current?.click()}>Import</button>
          <input ref={importRef} type="file" accept=".json" style={{ display: 'none' }} onChange={handleImport} />
        </div>
      </div>

      {/* Run All + Progress */}
      <div className={styles.controlsRow}>
        <button
          className={styles.runAllBtn}
          onClick={handleRunAll}
          disabled={testCases.length === 0 || isRunning}
        >
          Run All Models
        </button>
        {isRunning && (
          <button className={styles.abortBtn} onClick={() => { abortRef.current = true; }}>Stop</button>
        )}
        {runProgress && (
          <span className={styles.progressLabel}>
            {runProgress.done}/{runProgress.total}
          </span>
        )}
        {runningLabel && (
          <span className={styles.runningLabel}>{runningLabel}</span>
        )}
      </div>

      {runProgress && (
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${(runProgress.done / runProgress.total) * 100}%` }}
          />
        </div>
      )}

      {/* Empty state */}
      {testCases.length === 0 && (
        <div className={styles.emptyState}>
          No test cases yet. Use "Save Blunder" in the replay panel to add one.
        </div>
      )}

      {/* Blunder cards */}
      {testCases.map(tc => {
        const isExpanded = expandedId === tc.id;
        const results = allResults[tc.id] || {};
        const tested = Object.keys(results).length;
        const passed = Object.values(results).filter(r => !r.isBlunder).length;
        const failed = Object.values(results).filter(r => r.isBlunder).length;
        const noBlunderDefined = !tc.blunderMove;

        return (
          <div key={tc.id} className={styles.blunderCard}>
            {/* Card header — click to expand */}
            <div
              className={styles.cardHeader}
              onClick={() => setExpandedId(isExpanded ? null : tc.id)}
            >
              <span className={styles.cardArrow}>{isExpanded ? '\u25BC' : '\u25B6'}</span>
              <span className={styles.cardName}>{tc.name}</span>
              <span className={styles.cardSeverity} style={{ color: severityColor[tc.severity] }}>
                {tc.severity}
              </span>
              {tested > 0 && (
                <span className={styles.cardStats}>
                  <span className={styles.cardStatsPass}>{passed}</span>
                  /
                  <span className={styles.cardStatsFail}>{failed}</span>
                  /{tested}
                </span>
              )}
            </div>

            {isExpanded && (
              <div className={styles.cardBody}>
                {/* Meta */}
                <div className={styles.cardMeta}>
                  {tc.testSide === 'light' ? 'Light' : 'Dark'} to move
                  {tc.blunderMove && ` \u2022 Blunder: ${tc.blunderMove.from}\u2192${tc.blunderMove.to}`}
                </div>
                {tc.expectedNote && (
                  <div className={styles.cardExpected}>Expected: {tc.expectedNote}</div>
                )}
                {tc.description && tc.description !== tc.name && (
                  <div className={styles.cardDescription}>{tc.description}</div>
                )}

                {/* Run this blunder button */}
                <div className={styles.cardActions}>
                  <button
                    className={styles.runBlunderBtn}
                    onClick={() => handleRunBlunder(tc)}
                    disabled={isRunning}
                  >
                    Run All 21
                  </button>
                  <button
                    className={styles.cardBtn}
                    onClick={() => onLoadPosition(tc.fen, tc.lightKing, tc.darkKing)}
                  >
                    Load Position
                  </button>
                  <button
                    className={`${styles.cardBtn} ${styles.cardBtnDelete}`}
                    onClick={() => handleDelete(tc.id)}
                    disabled={isRunning}
                  >
                    Delete
                  </button>
                </div>

                {/* No blunder move warning */}
                {noBlunderDefined && (
                  <div className={styles.noBlunderNote}>
                    No blunder move defined — all tests will show as "pass" (manual review only)
                  </div>
                )}

                {/* Variant grid */}
                <div className={styles.variantGrid}>
                  {ALL_VARIANTS.map(v => {
                    const result = results[v.key];
                    const isCellSelected = selectedCell?.testCaseId === tc.id && selectedCell?.variantKey === v.key;
                    let cellClass = styles.variantCell;
                    if (result) {
                      cellClass += result.isBlunder ? ` ${styles.variantCellFail}` : ` ${styles.variantCellPass}`;
                    }
                    if (isCellSelected) cellClass += ` ${styles.variantCellSelected}`;

                    return (
                      <button
                        key={v.key}
                        className={cellClass}
                        onClick={() => setSelectedCell(
                          isCellSelected ? null : { testCaseId: tc.id, variantKey: v.key }
                        )}
                        disabled={!result}
                        title={result ? `${v.label}: ${result.chosenMove.description}` : `${v.label}: not tested`}
                      >
                        <span className={styles.cellName}>{v.label}</span>
                        {result && (
                          <span className={styles.cellIcon}>
                            {result.isBlunder ? '\u2717' : '\u2713'}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Summary */}
                {tested > 0 && (
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryPass}>{passed} pass</span>
                    <span className={styles.summaryFail}>{failed} fail</span>
                    <span className={styles.summaryTotal}>{tested}/21 tested</span>
                  </div>
                )}

                {/* Cell detail */}
                {selectedCell?.testCaseId === tc.id && (() => {
                  const r = results[selectedCell.variantKey];
                  if (!r) return null;
                  const vLabel = ALL_VARIANTS.find(v => v.key === selectedCell.variantKey)?.label || selectedCell.variantKey;
                  return (
                    <div className={`${styles.cellDetail} ${r.isBlunder ? styles.cellDetailFail : styles.cellDetailPass}`}>
                      <div className={styles.cellDetailHeader}>
                        <span className={styles.cellDetailName}>{vLabel}</span>
                        <span className={r.isBlunder ? styles.cellDetailBadgeFail : styles.cellDetailBadgePass}>
                          {r.isBlunder ? 'FAIL' : 'PASS'}
                        </span>
                      </div>
                      <div className={styles.cellDetailMove}>{r.chosenMove.description}</div>
                      <div className={styles.cellDetailStats}>
                        eval: {r.evaluation} &bull; {(r.confidence * 100).toFixed(0)}% conf &bull; {(r.thinkingTime / 1000).toFixed(1)}s
                      </div>
                      {r.reasoning && (
                        <div className={styles.cellDetailReasoning}>{r.reasoning}</div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
